import { descargarExcel, num, txt, type Hoja } from './exportar';
import { COLUMNAS_EDITABLES, COLUMNAS_REFERENCIA } from './planilla';
import { inventoryStatus, orderStatus, severity } from './status';
import type {
  FichaProducto,
  ForecastSku,
  Product,
  Proveedor,
  PurchaseOrder,
  Recommendation,
  UnmetDemandRecord,
} from '@shared/types';

/**
 * Qué se baja de cada pantalla. Uno por tabla, y todos con la misma forma.
 *
 * ⚠️ Los números van **sin formatear**: en la pantalla `1.234` se lee mejor, pero en una celda
 * es texto — no se puede sumar, ni ordenar, ni volver a subir. Ver `exportar.ts`.
 */

/** Semáforo de inventario. Es además la base de la planilla de carga. */
export function exportarInventario(items: Product[], proveedores: Proveedor[] = []): void {
  const nombreProv = new Map(proveedores.map((p) => [p.id, p.nombre]));
  descargarExcel('semaforo-inventario', [
    {
      nombre: 'Inventario',
      anchos: [16, 40, 18, 16, 12, 12, 14, 14, 16, 12, 22],
      filas: items.map((p) => ({
        [COLUMNAS_REFERENCIA.sku]: p.sku,
        [COLUMNAS_REFERENCIA.nombre]: p.name,
        [COLUMNAS_REFERENCIA.categoria]: p.category,
        [COLUMNAS_REFERENCIA.marca]: p.supplier,
        [COLUMNAS_REFERENCIA.estado]: inventoryStatus[p.status].label,
        [COLUMNAS_REFERENCIA.stock]: num(p.currentStock),
        'En transito': num(p.inTransit),
        [COLUMNAS_REFERENCIA.cobertura]: num(p.coverageDays),
        [COLUMNAS_REFERENCIA.demanda]: num(p.demandPerDay),
        // Vacío y no cero: un cero se lee como "cuesta cero", que es justo lo que la app evita
        // decir en pantalla con su «sin costo».
        [COLUMNAS_EDITABLES.costo]: num(p.unitCost, false),
        [COLUMNAS_EDITABLES.leadTime]: num(p.leadTimeDays),
        [COLUMNAS_EDITABLES.moq]: num(p.moq),
        [COLUMNAS_EDITABLES.proveedor]: txt(p.supplierId == null ? '' : nombreProv.get(p.supplierId)),
        // `null` = automático, y baja VACÍO. Un 0 sería mentir: 0 es "sin colchón".
        [COLUMNAS_EDITABLES.colchonPct]: num(p.safetyPct),
        [COLUMNAS_EDITABLES.reorden]: num(p.reorderPoint),
      })),
    },
  ]);
}

/**
 * La PLANILLA DE CARGA: la que se baja, se completa y se vuelve a subir.
 *
 * Lleva las mismas columnas que el semáforo pero con una diferencia que importa: **una hoja
 * aparte con las instrucciones**, porque quien la completa no es quien la programó y de otro
 * modo no hay dónde decirle qué columnas se aplican y cuáles se ignoran.
 */
export function exportarPlanilla(items: Product[], proveedores: Proveedor[]): void {
  const nombreProv = new Map(proveedores.map((p) => [p.id, p.nombre]));
  const hojas: Hoja[] = [
    {
      nombre: 'Productos',
      anchos: [16, 42, 18, 16, 12, 14, 14, 16, 18, 12, 26, 20, 20],
      filas: items.map((p) => ({
        [COLUMNAS_REFERENCIA.sku]: p.sku,
        [COLUMNAS_REFERENCIA.nombre]: p.name,
        [COLUMNAS_REFERENCIA.categoria]: p.category,
        [COLUMNAS_REFERENCIA.marca]: p.supplier,
        [COLUMNAS_REFERENCIA.stock]: num(p.currentStock),
        [COLUMNAS_REFERENCIA.demanda]: num(p.demandPerDay),
        [COLUMNAS_REFERENCIA.cobertura]: num(p.coverageDays),
        [COLUMNAS_EDITABLES.costo]: num(p.unitCost, false),
        [COLUMNAS_EDITABLES.leadTime]: num(p.leadTimeDays),
        [COLUMNAS_EDITABLES.moq]: num(p.moq),
        [COLUMNAS_EDITABLES.proveedor]: txt(p.supplierId == null ? '' : nombreProv.get(p.supplierId)),
        [COLUMNAS_EDITABLES.colchonPct]: num(p.safetyPct),
        [COLUMNAS_EDITABLES.reorden]: num(p.reorderPoint),
      })),
    },
    {
      nombre: 'Como completarla',
      anchos: [34, 76],
      filas: [
        { Tema: 'Que hago con este archivo', Detalle: 'Completa las columnas de la derecha y volve a subirlo en Importar Datos. Solo se aplican esas seis; el resto esta para que sepas de que producto se trata.' },
        { Tema: 'Columnas que SI se aplican', Detalle: `${COLUMNAS_EDITABLES.costo} · ${COLUMNAS_EDITABLES.leadTime} · ${COLUMNAS_EDITABLES.moq} · ${COLUMNAS_EDITABLES.proveedor} · ${COLUMNAS_EDITABLES.colchonPct} · ${COLUMNAS_EDITABLES.reorden}` },
        { Tema: 'Columnas que se ignoran', Detalle: 'Stock, demanda y cobertura las escribe el sistema desde el B2B. Si las cambias aca, no pasa nada: se respeta el dato real.' },
        { Tema: 'Una celda vacia', Detalle: 'NO borra el dato: quiere decir "no lo toques". Para vaciar un valor hay que escribir 0.' },
        { Tema: 'La columna SKU', Detalle: 'No la toques: es la que dice a que producto corresponde cada fila. Si un SKU no existe, esa fila se informa y se saltea.' },
        { Tema: 'Proveedor', Detalle: `Escribi el nombre EXACTO de uno de los proveedores cargados. Los que hay hoy: ${proveedores.map((p) => p.nombre).join(' · ') || '(ninguno todavia)'}` },
        { Tema: 'Tiempo de produccion', Detalle: 'Si el producto tiene proveedor, su tiempo sale del proveedor. Esta columna sirve para los que no tienen ninguno.' },
        { Tema: 'Colchon (% de la espera)', Detalle: 'Cuanto colchon queres para ese producto, como porcentaje de lo que se vende mientras el proveedor fabrica. Con 20 y una espera de 100 dias vendiendo 10 por dia, el colchon queda en 200 unidades. Vacio = lo calcula el sistema solo.' },
        { Tema: 'Punto de reorden (uds)', Detalle: 'El nivel de stock al que hay que pedir. Si lo escribis, MANDA sobre el que calcula el sistema. Vacio = lo calcula el sistema.' },
        { Tema: 'Volver algo a automatico', Detalle: 'Desde este archivo NO se puede: una celda vacia significa "no lo toques". Para que el sistema vuelva a calcular el colchon o el reorden de un producto, vacia la celda en Configuracion > Configuracion por Producto.' },
        { Tema: 'Podes borrar filas', Detalle: 'Solo se actualiza lo que este en el archivo. Si borras las filas que no queres tocar, mas rapido y menos riesgo.' },
      ],
    },
  ];
  descargarExcel('planilla-de-carga', hojas);
}

/** Detalle por producto del forecast: de dónde sale cada número. */
export function exportarForecast(items: ForecastSku[]): void {
  descargarExcel('forecast-por-producto', [
    {
      nombre: 'Forecast',
      anchos: [16, 40, 16, 14, 12, 12, 14, 14, 14, 14, 12, 70],
      filas: items.map((i) => ({
        SKU: i.sku,
        Producto: i.name,
        'Como vende': i.forma,
        Metodo: i.metodo,
        'Demanda por dia': num(i.demandaDiaria),
        'Desvio diario': num(i.sigmaDiaria),
        'Stock actual': num(i.currentStock),
        'Stock de seguridad': num(i.stockSeguridad),
        'Punto de reorden': num(i.puntoReorden),
        'Dias hasta reorden': num(i.diasHastaReorden),
        'Cuanto comprar': num(i.cantidadSugerida),
        'Tiempo de produccion (dias)': num(i.leadTimeDays),
        'De donde sale': i.razon,
      })),
    },
  ]);
}

/** Recomendaciones de compra. */
export function exportarRecomendaciones(items: Recommendation[]): void {
  descargarExcel('recomendaciones-de-compra', [
    {
      nombre: 'Recomendaciones',
      anchos: [16, 40, 18, 18, 12, 14, 12, 14, 16, 14, 70],
      filas: items.map((r) => ({
        SKU: r.sku,
        Producto: r.productName,
        Categoria: r.category,
        Proveedor: r.supplier,
        Urgencia: severity[r.severity].label,
        'Ordenar antes del': r.orderBefore,
        Vencido: r.overdue ? 'si' : 'no',
        'Stock actual': num(r.currentStock),
        'Cobertura (dias)': num(r.coverageDays),
        'Cuanto comprar': num(r.qtyRecommended),
        'Costo estimado': num(r.costEstimate, false),
        Estado: r.status,
        'Decidido por': txt(r.decidedBy),
        'Por que': r.rationale,
      })),
    },
  ]);
}

/** Órdenes de compra. */
export function exportarOrdenes(items: PurchaseOrder[]): void {
  descargarExcel('ordenes-de-compra', [
    {
      nombre: 'Ordenes',
      anchos: [22, 16, 40, 22, 12, 16, 18, 16],
      filas: items.map((o) => ({
        'N de orden': o.ocNumber,
        SKU: o.sku,
        Producto: o.productName,
        Proveedor: o.supplier,
        Cantidad: num(o.quantity),
        'Total COP': num(o.totalCop, false),
        Estado: orderStatus[o.status].label,
        'Entrega estimada': o.deliveryEstimate,
      })),
    },
  ]);
}

/** Demanda insatisfecha: lo que un cliente pidió y no se le pudo entregar. */
export function exportarDemanda(items: UnmetDemandRecord[]): void {
  descargarExcel('demanda-insatisfecha', [
    {
      nombre: 'No entregado',
      anchos: [14, 16, 40, 14, 14, 14, 16],
      filas: items.map((d) => ({
        Fecha: d.date,
        SKU: d.sku,
        Producto: d.productName,
        Solicitado: num(d.requested),
        Entregado: num(d.delivered),
        'No entregado': num(d.notDelivered),
        'Perdido estimado (COP)': num(d.lostValue, false),
      })),
    },
  ]);
}

/**
 * Reporte de UN producto: todo lo que el sistema sabe de él, en varias hojas.
 *
 * Es la ficha de la pantalla, pero para llevarse: sirve para mandarle a un proveedor el
 * respaldo de por qué se le está comprando esa cantidad.
 */
export function exportarFicha(d: FichaProducto): void {
  const p = d.producto;
  const hojas: Hoja[] = [
    {
      nombre: 'Resumen',
      anchos: [30, 60],
      filas: [
        { Dato: 'SKU', Valor: p.sku },
        { Dato: 'Producto', Valor: p.name },
        { Dato: 'Categoria', Valor: p.category },
        { Dato: 'Marca', Valor: p.supplier },
        { Dato: 'Estado', Valor: inventoryStatus[p.status].label },
        { Dato: 'Stock actual', Valor: num(p.currentStock) },
        { Dato: 'Cobertura (dias)', Valor: num(p.coverageDays) },
        { Dato: 'Demanda por dia', Valor: num(p.demandPerDay) },
        { Dato: 'Tiempo de produccion (dias)', Valor: num(p.leadTimeDays) },
        { Dato: 'Valor unitario', Valor: num(p.unitCost, false) },
        ...(d.forecast
          ? [
              { Dato: 'Stock de seguridad', Valor: num(d.forecast.stockSeguridad) },
              { Dato: 'Punto de reorden', Valor: num(d.forecast.puntoReorden) },
              { Dato: 'Cuanto comprar', Valor: num(d.forecast.cantidadSugerida) },
              { Dato: 'Dias hasta reorden', Valor: num(d.forecast.diasHastaReorden) },
              { Dato: 'De donde sale', Valor: d.forecast.razon },
            ]
          : [{ Dato: 'Forecast', Valor: 'Sin serie suficiente para proyectar' }]),
      ],
    },
    {
      nombre: 'Venta diaria',
      anchos: [14, 12],
      filas: d.serie.map((s) => ({ Fecha: s.date, Unidades: num(s.units) })),
    },
  ];
  if (d.ordenes.length) {
    hojas.push({
      nombre: 'Ordenes',
      anchos: [22, 12, 16, 16, 18],
      filas: d.ordenes.map((o) => ({
        'N de orden': String(o.oc_number),
        Cantidad: num(Number(o.quantity)),
        'Total COP': num(Number(o.total_cop), false),
        Estado: String(o.status),
        'Entrega estimada': String(o.delivery_estimate),
      })),
    });
  }
  if (d.faltantes.length) {
    hojas.push({
      nombre: 'No entregado',
      anchos: [14, 14, 14, 14],
      filas: d.faltantes.map((f) => ({
        Fecha: f.date,
        Solicitado: num(f.requested),
        Disponible: num(f.available),
        Faltaron: num(f.gap),
      })),
    });
  }
  descargarExcel(`producto-${p.sku}`, hojas);
}
